/**
 * The panel, as a static page (PLAN §20).
 *
 * One bundle, one HTML file, and a router that is a `switch` over the path.
 * Cloudflare Pages serves `index.html` for every address — that is what the
 * `_redirects` file is for — so a deep link and a refresh both land here and
 * this decides what they meant.
 *
 * **Every screen renders and submits without a server.** That is the whole
 * change: the old panel worked with JavaScript switched off and this one cannot
 * (§8.2). Not a real constraint for an internal panel, but a real loss, and
 * listed as one rather than discovered as a bug.
 *
 * The order at boot matters. The catalogue is fetched *before* anything is
 * drawn, because a screen that appears and then fills in its own words a moment
 * later is worse than a screen that appears late.
 */

import { configured, MAX_IMPORT_ROWS } from './config.ts';
import { el, replace } from './dom.ts';
import { loadCatalogue, translate as t } from './i18n.ts';
import { currentSession, onSessionChange, type SignedIn } from './session.ts';
import { renderLogin } from './screens/login.ts';
import { renderHistory } from './screens/history.ts';
import { renderStatus } from './screens/status.ts';
import { renderNewCampaign } from './screens/new-campaign.ts';
import { renderConfirm } from './screens/confirm.ts';

const root = document.getElementById('app') as HTMLElement;

async function boot(): Promise<void> {
  if (!configured()) {
    // Built without `--define`. Worth saying out loud, because the alternative
    // is a login screen that refuses every password for no visible reason.
    root.textContent = 'This build has no project configured (SUPABASE_URL is empty).';
    return;
  }

  await loadCatalogue();

  // A session that ends anywhere — expiry, another tab, sign-out — puts every
  // tab back to the login screen rather than leaving one showing a list it can
  // no longer refresh.
  onSessionChange(() => void route());

  window.addEventListener('popstate', () => void route());
  await route();
}

async function route(): Promise<void> {
  const session = await currentSession();
  if (session === undefined) {
    renderLogin(root, () => void route());
    return;
  }

  const path = window.location.pathname;

  if (path === '/' || path === '') return go('/campaigns');
  if (path === '/campaigns') return renderHistory(root, session.email);

  if (path === '/campaign/new') return renderNewCampaign(root, session.email, MAX_IMPORT_ROWS);

  const confirm = path.match(/^\/campaign\/(\d+)\/confirm$/);
  if (confirm !== null) return renderConfirm(root, session.email, Number(confirm[1]));

  const campaign = path.match(/^\/campaign\/(\d+)$/);
  if (campaign !== null) return renderStatus(root, session.email, Number(campaign[1]));

  notFound(session);
}

function go(path: string): void {
  window.history.replaceState({}, '', path);
  void route();
}

function notFound(session: SignedIn): void {
  void session;
  replace(
    root,
    el(
      'main',
      { class: 'page' },
      el(
        'section',
        { class: 'card' },
        el('h1', {}, t('error.title', { status: 404 })),
        el('p', {}, t('error.notFound')),
        el('a', { href: '/campaigns' }, t('error.backHome')),
      ),
    ),
  );
}

void boot();
